/**
 * `Crate` — the home route (`/`), the full channel catalogue. `docs/design-direction.md`
 * §8.2 ("the crate"), `spec.md` §6.4.
 *
 * This is the single most-used screen in the product: a ledger, not a card grid. The
 * masthead prints `SELECTION` over a graded halftone with the **true** count and
 * runtime, a horizontally scrolling strip of filter facets sits under it, and the body
 * is a virtualised ledger of 56px `TrackRow`s — because the catalogue can hold
 * thousands of rows and rendering all of them is the one thing this screen must never
 * do.
 *
 * ## Virtualisation is not optional here
 *
 * `components/VirtualList` windows the DOM to the scrollport plus a small overscan
 * regardless of how many rows the catalogue holds (5000 fixture rows keep the DOM
 * under 40 nodes — T41 acceptance 1). Row heights are declared, not measured: 56px for
 * a track, 48px for a sticky day header, 88px for a flyer divider, exactly the three
 * values `VirtualList`'s own header names.
 *
 * ## The dividers are real drops, not a decorative tempo
 *
 * `GET /api/catalogue` returns `sections: CatalogueSection[]` — `kind: 'day'` groups by
 * posting day (§6.4), `kind: 'drop'` by the media group a channel actually posted
 * together. {@link buildCrateRows} interleaves them into the row list at their real
 * `afterIndex`, so the sticky 48px day header and the full-bleed `FlyerDivider` mark
 * the sessions a plant actually cut records in, not a fabricated cadence. A catalogue
 * whose drops land every six tracks — which is exactly what T41 acceptance 2 exercises
 * — produces a flyer at combined row indices 6, 13, 20, …; that is a property of the
 * data, reproduced faithfully, not a hard-coded "every seventh row".
 *
 * Track rows carry their own **bar line**: `TrackRow`'s own `index` prop drives
 * `--border-strong` on every fourth track, counted across the tracks alone so the
 * metre survives a drop landing between two of them.
 *
 * ## The BPM cell lives outside `TrackRow`, on purpose
 *
 * `TrackRow` (T36, already shipped) folds its `meta` array into one joined line and has
 * no BPM cell of its own to hide. Rather than editing a component this task does not
 * own, the BPM figure is printed as a **second grid column** beside `TrackRow` inside
 * this screen's own `<li>` — `styles.bpm` — which is what the `@container` query in
 * `crate.module.css` actually targets: below 360px of the ledger's own inline size the
 * column is dropped, never squeezed (T41 acceptance 5). `TrackRow` itself keeps only
 * the performer and the year in its meta line, because riddim is not part of
 * `TrackSummary` and BPM has its own column — omission, never a placeholder.
 *
 * ## The filter strip prints real facets, honestly
 *
 * `GET /api/catalogue`'s query surface is `sort · seed · year · artistId · tagId ·
 * minDuration · maxDuration · hasCover` (`api-groundtruth.md`, `schemas/catalogue.ts`).
 * There is no BPM range parameter and no 7"/12" format field anywhere on the wire, so a
 * chip that claimed to filter by either would be a control that changes nothing — the
 * exact "chip that is not a control" `Chip`'s own header warns against. The five chips
 * print the vocabulary §8.2 asks for (`ГОД`, `РИДДИМ`, `ЛЕЙБЛ`, `BPM`, `7" / 12"`) as
 * **static markers** — the same contract `SIDE A` uses on the Now Playing catalogue
 * plate — so the strip is real, honest print rather than a promise the API cannot keep.
 * Wiring a genuine value picker for year/tag is future work for a dedicated filter
 * sheet, not a blind toggle bolted on here.
 *
 * ## The ledger stagger fires once per screen mount, not once per row mount
 *
 * §7.2's rule is "18ms apart, capped at 12 rows, once per mount" — and a virtualised
 * list unmounts and remounts the same row every time it scrolls out of and back into
 * the window, so a stagger keyed only on a row's index would replay every time the user
 * scrolls back to the top. `contentMountedRef` flips exactly once, the first time the
 * ledger actually has rows to show (the catalogue loads asynchronously, so the
 * screen's very first commit is the `Loading` skeleton with no `TrackRow` in the tree
 * at all — flipping on THAT commit would mean the stagger never fires even once).
 * `renderRow` reads the ref fresh on every call — it is a plain closure over a ref, not
 * a value captured at some earlier render — so any render pass that happens after the
 * flip, including one a scroll triggers, stops handing out the stagger class even to a
 * row index under the cap (T41 acceptance 3).
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';

import { hasCyrillic } from '@bl/shared';
import type { CatalogueSection, TrackSummary } from '@bl/shared';

import type { ScreenProps } from '../app/router';
import { ErrorState } from '../app/states/ErrorState';
import { ResumeBanner } from '../app/states/ResumeBanner';
import { Empty } from '../app/states/Empty';
import { Loading, StateFrame } from '../app/states/Loading';
import type { Wiring } from '../app/wiring';
import { getWiring } from '../app/wiring';
import { FlyerDivider } from '../components/FlyerDivider';
import { MORE_LABEL, TrackRow } from '../components/TrackRow';
import { Chip } from '../components/primitives/Chip';
import { Halftone } from '../components/primitives/Halftone';
import { cx } from '../components/primitives/Rule';
import type { VirtualRow, VisibleRange } from '../components/VirtualList';
import { VirtualList } from '../components/VirtualList';
import type { CatalogueArgs } from '../lib/api';
import type { TelegramFacade } from '../lib/telegram';
import { telegram as defaultTelegram } from '../lib/telegram';
import type { CataloguePage } from '../store/library';
import { selectCataloguePage, selectIsFavorite, useLibraryStore } from '../store/library';
import { usePlayerStore } from '../store/player';
import { useQueueStore } from '../store/queue';
import { useResumeStore } from '../store/resume';

import styles from './crate.module.css';

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

/** Label print, Latin caps (§4.3, §12.16). The Russian mono line beneath it is what
 *  makes this English word legible to a screen reader in context (§11). */
export const CRATE_TITLE = 'SELECTION';

/** The screen's Russian identity — the nav-strip root's own name (`router.tsx`). */
export const CRATE_LABEL_RU = 'Ящик';

export const CRATE_FILTERS_LABEL_RU = 'Фильтры ящика';

/** §8.2's filter strip vocabulary, verbatim. See the file header for why these are
 *  printed as static markers rather than wired toggles. */
export const CRATE_FILTER_LABELS: readonly string[] = Object.freeze([
  'ГОД',
  'РИДДИМ',
  'ЛЕЙБЛ',
  'BPM',
  '7" / 12"',
]);

/** The catalogue query this screen loads. A module constant, not a literal recreated
 *  every render, so it is a stable effect dependency with no `useMemo` required. */
const ARGS: CatalogueArgs = {};

/* ══ row geometry (`VirtualList`'s own three declared heights) ═══════════════════════ */

export const TRACK_ROW_HEIGHT = 56;
export const SECTION_HEADER_HEIGHT = 48;
export const FLYER_HEIGHT = 88;

/** §7.2: 18ms apart, capped at 12 rows, once per screen mount. */
export const STAGGER_CAP = 12;
export const STAGGER_STEP_MS = 18;

/** How many rows of run-way to keep loaded ahead of the visible window. */
const PAGE_AHEAD_ROWS = 12;

/* ══ formatting (masthead honesty — never a fabricated number, §12.6) ════════════════ */

/**
 * `07:41:22` — the crate's own runtime, hours always printed, always two digits.
 *
 * Not `components/Scrub`'s `formatTimecode`: that is a *playhead* format that drops the
 * hour field below an hour, and a masthead runtime is a fixed-width figure. Local to
 * this screen for the same reason `NowPlaying.formatAdded` and `MyCrate.formatRuntime`
 * are each their own small formatter rather than a shared one — screens stay decoupled.
 */
export function formatRuntime(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return [hours, minutes, secs].map((part) => String(part).padStart(2, '0')).join(':');
}

/**
 * The masthead's mono line: the true count, and the runtime **only** once every row is
 * loaded (`nextCursor === null`) — summing the durations of one page is not the
 * catalogue's runtime, and printing it anyway would be exactly the fabricated number
 * §12.6 bans. With neither fact available the line is absent (T41 acceptance 6).
 */
export function crateMetaLine(
  total: number | null,
  items: readonly TrackSummary[],
  complete: boolean,
): string | null {
  const parts: string[] = [];
  if (total !== null) parts.push(`${String(total)} записей`);
  if (complete && items.length > 0) {
    parts.push(formatRuntime(items.reduce((sum, item) => sum + item.durationS, 0)));
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/* ══ the row model ═════════════════════════════════════════════════════════════════ */

export interface CrateTrackRow extends VirtualRow {
  readonly kind: 'track';
  readonly track: TrackSummary;
  /** Position among tracks alone — the bar line's own metre, independent of dividers. */
  readonly trackIndex: number;
}

export interface CrateSectionRow extends VirtualRow {
  readonly kind: 'day' | 'drop';
  readonly label: string;
  readonly count: number;
}

export type CrateRow = CrateTrackRow | CrateSectionRow;

function sectionRow(section: CatalogueSection, key: string): CrateSectionRow {
  return {
    key,
    height: section.kind === 'day' ? SECTION_HEADER_HEIGHT : FLYER_HEIGHT,
    kind: section.kind,
    label: section.label,
    count: section.count,
  };
}

/**
 * Interleave the catalogue's own `sections` into the row list at their real
 * `afterIndex` — `-1` prints before the first track, `n` after the `n`-th (0-based).
 *
 * A stable, generic algorithm over real server data: it does not know or care that a
 * catalogue whose drops land every six tracks produces a flyer at combined indices
 * 6, 13, 20, … — that pattern falls out of the data, exactly as `spec.md` §6.4
 * describes a drop divider, rather than being asserted here as a fixed period.
 */
export function buildCrateRows(
  items: readonly TrackSummary[],
  sections: readonly CatalogueSection[] = [],
): CrateRow[] {
  const after = new Map<number, CatalogueSection[]>();
  for (const section of sections) {
    const list = after.get(section.afterIndex);
    if (list === undefined) after.set(section.afterIndex, [section]);
    else list.push(section);
  }

  const rows: CrateRow[] = [];

  let sectionSeq = 0;
  const pushSections = (afterIndex: number): void => {
    for (const section of after.get(afterIndex) ?? []) {
      sectionSeq += 1;
      rows.push(sectionRow(section, `section-${String(sectionSeq)}-${section.kind}`));
    }
  };

  pushSections(-1);
  items.forEach((track, trackIndex) => {
    rows.push({
      key: `track-${String(track.id)}`,
      height: TRACK_ROW_HEIGHT,
      kind: 'track',
      track,
      trackIndex,
    });
    pushSections(trackIndex);
  });

  return rows;
}

/* ══ deps ══════════════════════════════════════════════════════════════════════════ */

export interface CrateDeps {
  readonly library?: typeof useLibraryStore;
  readonly queue?: typeof useQueueStore;
  readonly player?: typeof usePlayerStore;
  readonly wiring?: Pick<Wiring, 'select'>;
  readonly telegram?: TelegramFacade;
}

export interface CrateProps extends Partial<ScreenProps> {
  readonly deps?: CrateDeps;
}

const EMPTY_PAGE: CataloguePage = Object.freeze({
  items: [],
  nextCursor: null,
  total: null,
  status: 'idle',
  loadingMore: false,
  error: null,
  sections: [],
});

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

export function Crate({ deps = {} }: CrateProps = {}): JSX.Element {
  const library = deps.library ?? useLibraryStore;
  const queue = deps.queue ?? useQueueStore;
  const player = deps.player ?? usePlayerStore;
  const tg = deps.telegram ?? defaultTelegram;

  const page = library((state) => selectCataloguePage(ARGS)(state)) ?? EMPTY_PAGE;

  /*
   * Subscribed to the whole favourites map, not to one id: the rows are rendered from a
   * virtualised callback, so there is nowhere to put a hook per row. Filing a record has
   * to repaint its bracket, and nothing else on this screen changes when it does — the
   * catalogue page is untouched — so without this subscription the control would look
   * dead. The value is still read through `selectIsFavorite`, the store's single
   * sanctioned read; this subscription is simply what it reads FROM.
   */
  const favorites = library((state) => state.favorites);
  const currentTrackId = player((state) => state.trackId);
  const isPlaying = player((state) => state.isPlaying);

  useEffect(() => {
    void library.getState().loadCatalogue(ARGS);
  }, [library]);

  const items = page.items;

  /*
   * The stagger flips exactly once — the first time the ledger actually has rows to
   * show, not merely once the screen has mounted. The catalogue loads asynchronously,
   * so the screen's very first commit is the `Loading` skeleton with no `TrackRow` in
   * the tree at all; flipping on that commit (rather than on this one) would mean the
   * stagger class is never handed out even once. No dependency array: the effect is a
   * cheap idempotent ref write that runs after every commit, and it is a no-op once
   * `items.length > 0` has been true one time. See the file header for why a ref check
   * — read fresh inside `renderRow` on every call — is what stops a later scroll from
   * replaying it.
   */
  const contentMountedRef = useRef(false);
  useEffect(() => {
    if (items.length > 0) contentMountedRef.current = true;
  });
  const rows = useMemo<CrateRow[]>(
    () => buildCrateRows(items, page.sections),
    [items, page.sections],
  );

  const play = useCallback(
    async (index: number): Promise<void> => {
      tg.haptics.impact('light');
      await queue.getState().replace(items, index);
      const w = deps.wiring ?? getWiring();
      await w.select(index);
    },
    [deps.wiring, items, queue, tg],
  );

  /*
   * F13 / M6 cell 5. `boot()` publishes what `persistence.load()` reconciled; this is
   * where it becomes an offer the user can take. The banner lives at the top of the
   * crate — §8.8 makes it a state, not a dialog, so it may simply be scrolled past.
   *
   * The tap IS the gesture §13.2 requires, which is why `autoplay: true` is correct
   * here and would not have been at boot. `startAtMs` hands the position straight to
   * `select()`, so the element opens at the right offset instead of seeking after play.
   */
  const resumeOffer = useResumeStore((state) => state.offer);

  const onResume = useCallback((): void => {
    const offer = useResumeStore.getState().offer;
    if (offer === null) return;
    useResumeStore.getState().clear();
    tg.haptics.impact('light');
    const w = deps.wiring ?? getWiring();
    void w.select(offer.queueIndex, { autoplay: true, startAtMs: offer.positionMs });
  }, [deps.wiring, tg]);

  const onDismissResume = useCallback((): void => {
    useResumeStore.getState().clear();
  }, []);

  const toggleFile = useCallback(
    (trackId: number): void => {
      tg.haptics.impact('light');
      void library.getState().toggleFavorite(trackId);
    },
    [library, tg],
  );

  const queueNext = useCallback(
    (track: TrackSummary): void => {
      tg.haptics.impact('light');
      void queue.getState().enqueueNext([track]);
    },
    [queue, tg],
  );

  const retry = useCallback((): void => {
    void library.getState().loadCatalogue(ARGS);
  }, [library]);

  const onRangeChange = useCallback(
    (range: VisibleRange): void => {
      if (page.nextCursor === null || page.loadingMore) return;
      if (range.end >= rows.length - PAGE_AHEAD_ROWS) {
        void library.getState().loadMoreCatalogue(ARGS);
      }
    },
    [library, page.loadingMore, page.nextCursor, rows.length],
  );

  const renderRow = useCallback(
    (row: CrateRow): ReactNode => {
      /*
       * `row.kind === 'track'` is checked first, positively, rather than excluding
       * 'day' and 'drop' one at a time: TypeScript does not narrow a union whose other
       * member's discriminant is itself `'day' | 'drop'` through two separate negative
       * equality checks (verified against tsc directly), but it narrows cleanly on the
       * single-literal branch.
       */
      if (row.kind === 'track') {
        const { track, trackIndex } = row;
        const current = currentTrackId === track.id;
        const barline = (trackIndex + 1) % 4 === 0;
        const showStagger = !contentMountedRef.current && trackIndex < STAGGER_CAP;
        const style = showStagger
          ? ({ '--i': String(Math.min(trackIndex, STAGGER_CAP - 1)) } as CSSProperties)
          : undefined;

        return (
          <li
            className={cx(styles.trackCell, showStagger && styles.stagger)}
            style={style}
            data-track-id={String(track.id)}
            data-stagger={showStagger ? 'true' : undefined}
          >
            <TrackRow
              as="div"
              title={track.title}
              performer={track.performer}
              durationS={track.durationS}
              messageId={track.id}
              seed={track.plate.seed}
              meta={[track.year]}
              index={trackIndex}
              current={current}
              playing={current && isPlaying}
              onPlay={() => {
                void play(trackIndex);
              }}
              filed={selectIsFavorite(track.id)({ favorites })}
              onFile={() => {
                toggleFile(track.id);
              }}
              onMore={() => {
                queueNext(track);
              }}
              moreLabel={MORE_LABEL}
            />
            {track.bpm === null ? null : (
              <span
                className={cx('t-mono', 't-11', styles.bpm, barline && styles.bpmStrong)}
                data-bpm="true"
              >
                {`BPM ${String(track.bpm)}`}
              </span>
            )}
          </li>
        );
      }

      if (row.kind === 'day') {
        return (
          <li className={styles.sectionHeader} data-section="day">
            <span
              className={cx('t-mono', 't-13', hasCyrillic(row.label) && styles.cyr)}
              data-section-label="true"
            >
              {row.label}
            </span>
            <span className={cx('t-mono', 't-11', styles.sectionCount)} data-section-count="true">
              {row.count}
            </span>
          </li>
        );
      }

      return (
        <li className={styles.flyerRow} data-section="drop">
          <FlyerDivider title={row.label} />
        </li>
      );
    },
    [currentTrackId, favorites, isPlaying, play, queueNext, toggleFile],
  );

  const meta = crateMetaLine(
    page.total,
    items,
    page.status === 'ready' && page.nextCursor === null,
  );

  const masthead = (
    <header className={styles.masthead}>
      <Halftone as="div">
        <h1 className={cx('t-display', 't-28', styles.mastheadTitle)}>{CRATE_TITLE}</h1>
      </Halftone>

      {meta === null ? null : (
        <p className={cx('t-mono', 't-13', styles.cyr, styles.mastheadMeta)} data-crate-meta="true">
          {meta}
        </p>
      )}

      <div className={styles.filters} role="group" aria-label={CRATE_FILTERS_LABEL_RU}>
        {CRATE_FILTER_LABELS.map((label) => (
          <Chip key={label} label={label} className={styles.filterChip} />
        ))}
      </div>
    </header>
  );

  let body: JSX.Element;

  if (page.status === 'error' && page.error !== null && items.length === 0) {
    body = <ErrorState failure={page.error} grid="ledger" onRetry={retry} />;
  } else if (page.status === 'ready' && items.length === 0) {
    body = <Empty variant="crate" grid="ledger" />;
  } else if (items.length === 0) {
    body = <Loading grid="ledger" rows={6} />;
  } else {
    body = (
      <StateFrame grid="ledger" state="content" role="group" ariaLabel={CRATE_LABEL_RU}>
        <VirtualList<CrateRow>
          rows={rows}
          renderRow={renderRow}
          onRangeChange={onRangeChange}
          as="ul"
          innerClassName={styles.ledger}
        />
      </StateFrame>
    );
  }

  return (
    <div className={styles.screen} data-screen="crate">
      {masthead}
      {resumeOffer === null ? null : (
        <ResumeBanner
          title={resumeOffer.track.title}
          performer={resumeOffer.track.performer}
          positionMs={resumeOffer.positionMs}
          onResume={onResume}
          onDismiss={onDismissResume}
        />
      )}
      {body}
    </div>
  );
}
